import React, { useEffect, useState, useContext } from 'react';
import axios from 'axios';
import Compressor from 'compressorjs';
import defaultAvatar from '../assets/avatar.svg';
import { LanguageContext } from '../App';

function Profile() {
    const { language, setLanguage } = useContext(LanguageContext);
    const [profile, setProfile] = useState(null);
    const [newAvatar, setNewAvatar] = useState(null);
    const [isChanged, setIsChanged] = useState(false);
    const [subscription, setSubscription] = useState(null);
    const [editingField, setEditingField] = useState(null);
    const [newValue, setNewValue] = useState('');
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);

    // Languages available in the application
    const languages = [
        { code: 'en', name: 'English', flag: '🇺🇸' },
        { code: 'sr', name: 'Srpski', flag: '🇷🇸' },
        { code: 'es', name: 'Español', flag: '🇪🇸' },
        { code: 'fr', name: 'Français', flag: '🇫🇷' },
        { code: 'de', name: 'Deutsch', flag: '🇩🇪' },
        { code: 'ru', name: 'Руссий', flag: '🇷🇺' }
    ];

    // Translations
    const translations = {
        en: {
            profile: 'Profile',
            gender: 'Gender',
            style: 'Style',
            colors: 'Colors',
            city: 'City',
            email: 'Email',
            language: 'Language',
            saveProfile: 'Save Profile',
            settings: 'Settings',
            devBlockMessage: 'This block is currently under development, we will add this functionality soon',
            preferredLanguage: 'Preferred language for interface:',
            chooseLanguage: 'Choose the language you prefer.',
            preferredTime: 'Preferred time for daily outfit suggestions:',
            chooseDailyTime: 'Choose the time you would like to receive daily outfit recommendations.',
            uploadFavorite: 'Upload your favorite outfit photo:',
            clickToAdd: 'Click here to add a photo',
            uploadDescription: 'Upload a picture of your favorite outfit to help the AI learn your style preferences better.',
            saveSettings: 'Save Settings',
            subscriptionDetails: 'Subscription Details',
            freeAppMessage: 'Right now our app is absolutely free, due to beta testing',
            enjoyIt: 'Enjoy it 🤩',
            plan: 'Plan',
            price: 'Price',
            nextBillingDate: 'Next Billing Date',
            billingMethod: 'Billing Method',
            upgradePlan: 'Upgrade Plan',
            updateBilling: 'Update Billing Info',
            edit: 'Edit',
            saveChanges: 'Save changes',
            cancel: 'Cancel',
            loading: 'Loading profile and subscription...',
            error: 'Error',
            noProfileData: 'No profile or subscription data available.',
            emailExists: 'This email is already associated with another account.',
            emailUpdateSuccess: 'Email and associated looks updated successfully',
            emailUpdateFailed: 'Failed to update email and looks',
            fieldUpdateSuccess: '{field} updated successfully',
            fieldUpdateFailed: 'Failed to update {field}',
            errorUpdating: 'Error updating {field}',
            profileUpdateSuccess: 'Profile updated successfully',
            profileUpdateError: 'Error updating profile',
            addChange: 'Add / Change'
        },
        sr: {
            profile: 'Profil',
            gender: 'Pol',
            style: 'Stil',
            colors: 'Boje',
            city: 'Grad',
            email: 'Email',
            language: 'Jezik',
            saveProfile: 'Sačuvaj profil',
            settings: 'Podešavanja',
            devBlockMessage: 'Ovaj blok je trenutno u razvoju, uskoro ćemo dodati ovu funkcionalnost',
            preferredLanguage: 'Željeni jezik interfejsa:',
            chooseLanguage: 'Izaberite jezik koji preferirate.',
            preferredTime: 'Poželjno vreme za dnevne predloge outfita:',
            chooseDailyTime: 'Izaberite vreme kada biste želeli da primite dnevne preporuke outfita.',
            uploadFavorite: 'Otpremite fotografiju vašeg omiljenog outfita:',
            clickToAdd: 'Kliknite ovde da dodate fotografiju',
            uploadDescription: 'Otpremite sliku vašeg omiljenog outfita da pomognete AI da bolje nauči vaše stilske preference.',
            saveSettings: 'Sačuvaj podešavanja',
            subscriptionDetails: 'Detalji pretplate',
            freeAppMessage: 'Trenutno je naša aplikacija potpuno besplatna, zbog beta testiranja',
            enjoyIt: 'Uživajte 🤩',
            plan: 'Plan',
            price: 'Cena',
            nextBillingDate: 'Sledeći datum naplate',
            billingMethod: 'Način plaćanja',
            upgradePlan: 'Nadogradi plan',
            updateBilling: 'Ažuriraj informacije o plaćanju',
            edit: 'Uredi',
            saveChanges: 'Sačuvaj promene',
            cancel: 'Otkaži',
            loading: 'Učitavanje profila i pretplate...',
            error: 'Greška',
            noProfileData: 'Nema dostupnih podataka o profilu ili pretplati.',
            emailExists: 'Ovaj email je već povezan sa drugim nalogom.',
            emailUpdateSuccess: 'Email i povezani izgledi su uspešno ažurirani',
            emailUpdateFailed: 'Neuspelo ažuriranje emaila i izgledâ',
            fieldUpdateSuccess: '{field} uspešno ažuriran',
            fieldUpdateFailed: 'Neuspelo ažuriranje polja {field}',
            errorUpdating: 'Greška pri ažuriranju polja {field}',
            profileUpdateSuccess: 'Profil uspešno ažuriran',
            profileUpdateError: 'Greška pri ažuriranju profila',
            addChange: 'Dodaj / Promeni'
        }
    };

    // Get translation based on current language
    const t = translations[language] || translations.en;

    const getEmailFromCookies = () => {
        const email = document.cookie.replace(/(?:(?:^|.*;\s*)user_email\s*=s*([^;]*).*$)|^.*$/, "$1");
        console.log('Email from cookies:', email);
        return email;
    };

    useEffect(() => {
        const fetchProfile = async () => {
            try {
                const email = getEmailFromCookies();
                console.log('Email from cookies in Profile.js:', email);
                if (!email) {
                    console.error('User email is not available for fetching profile.');
                    setError('User email is not available');
                    setLoading(false);
                    return;
                }

                // Request profile
                const profileResponse = await axios.get(`/api/profile`, {
                    params: { email }
                });
                if (profileResponse.data) {
                    console.log('Profile data fetched:', profileResponse.data);
                    setProfile(profileResponse.data);
                    // If user has a preferred language, set it in the context
                    if (profileResponse.data.language) {
                        setLanguage(profileResponse.data.language);
                        console.log('User language set to:', profileResponse.data.language);
                    }
                } else {
                    console.error('Profile data not found.');
                    setError('Profile data not found');
                }

                // Try to get subscription, but use default if it fails
                try {
                    const subscriptionResponse = await axios.get(`/subscription`, {
                        params: { email }
                    });
                    if (subscriptionResponse.data) {
                        console.log('Subscription data fetched:', subscriptionResponse.data);
                        setSubscription(subscriptionResponse.data);
                    } else {
                        // Use default subscription data
                        setSubscription({
                            planName: "Free Beta",
                            price: "0",
                            nextBillingDate: new Date().setMonth(new Date().getMonth() + 1)
                        });
                    }
                } catch (subscriptionError) {
                    console.error('Error fetching subscription:', subscriptionError);
                    // Use default subscription data
                    setSubscription({
                        planName: "Free Beta",
                        price: "0",
                        nextBillingDate: new Date().setMonth(new Date().getMonth() + 1)
                    });
                }

                setLoading(false);
            } catch (error) {
                console.error('Error fetching profile:', error);
                setError('Error fetching profile data');
                
                // Use default subscription data
                setSubscription({
                    planName: "Free Beta",
                    price: "0",
                    nextBillingDate: new Date().setMonth(new Date().getMonth() + 1)
                });
                
                setLoading(false);
            }
        };

        fetchProfile();
    }, [setLanguage]);

    const handleAvatarChange = (event) => {
        const file = event.target.files[0];
        if (file) {
            new Compressor(file, {
                quality: 0.6,
                maxWidth: 300,
                maxHeight: 300,
                success: (compressedFile) => {
                    setNewAvatar(compressedFile);
                    setIsChanged(true);
                },
                error: (err) => {
                    console.error('Error compressing image:', err);
                },
            });
        }
    };

    const handleSaveProfile = async () => {
        try {
            const email = getEmailFromCookies();
            
            if (newAvatar) {
                // If we have a new avatar, use FormData and send to the updateProfile endpoint
                const formData = new FormData();
                formData.append('email', email);
                formData.append('gender', profile.gender);
                formData.append('style', profile.style);
                formData.append('colors', profile.colors);
                formData.append('city', profile.city);
                formData.append('language', language); // Include language field
                formData.append('avatar', newAvatar);
                
                const response = await axios.post('/updateProfile', formData, {
                    headers: {
                        'Content-Type': 'multipart/form-data',
                    },
                });
                
                setProfile({ ...profile, profile_image_url: response.data.profile_image_url, language });
                console.log('Profile updated with new avatar, language set to:', language);
            } else {
                // For updating language or other settings without avatar change
                const updateData = {
                    email: email,
                    field: 'language',
                    value: language
                };
                
                await axios.post('/updateProfileField', updateData);
                console.log('Language updated to:', language);
                
                // Also update any other profile settings that may have changed
                if (profile.dailyOutfitTime) {
                    await axios.post('/updateProfileField', {
                        email: email,
                        field: 'daily_outfit_time',
                        value: profile.dailyOutfitTime
                    });
                }
                
                setProfile({ ...profile, language });
            }
            
            setIsChanged(false);
            alert(t.profileUpdateSuccess);
        } catch (error) {
            console.error('Error updating profile:', error);
            alert(t.profileUpdateError);
        }
    };

    const handleLanguageChange = (e) => {
        setLanguage(e.target.value);
        setIsChanged(true);
        console.log('Language changed to:', e.target.value);
    };

    const handleEditClick = (field) => {
        setEditingField(field);
        setNewValue(profile[field]);
    };

    const handleFieldChange = (event) => {
        setNewValue(event.target.value);
    };

    const handleSaveField = async () => {
        if (editingField === 'email') {
            try {
                // Check if email exists
                const checkResponse = await axios.get('/checkEmailExists', {
                    params: { email: newValue }
                });
        
                if (checkResponse.data.exists) {
                    alert(t.emailExists);
                    return;
                }
        
                // Update email and looks
                const updateResponse = await axios.post('/updateUserEmailAndLooks', {
                    oldEmail: profile.email,
                    newEmail: newValue
                });
        
                if (updateResponse.status === 200) {
                    setProfile({ ...profile, email: newValue });
                    document.cookie = `user_email=${newValue}; path=/; expires=${new Date(new Date().setMonth(new Date().getMonth() + 1)).toUTCString()}`;
                    alert(t.emailUpdateSuccess);
                } else {
                    alert(t.emailUpdateFailed);
                }
            } catch (error) {
                console.error('Error updating email and looks:', error);
                alert(t.emailUpdateFailed);
            }
        } else {
            try {
                console.log(`Updating ${editingField} to: ${newValue}`);
                
                const updateData = {
                    email: profile.email,
                    field: editingField,
                    value: newValue
                };
                
                const response = await axios.post('/updateProfileField', updateData);
                
                if (response.data.success) {
                    const updatedValue = response.data.data ? response.data.data[editingField] : newValue;
                    
                    console.log(`Field ${editingField} updated successfully:`, updatedValue);
                    
                    setProfile(prevProfile => ({
                        ...prevProfile,
                        [editingField]: updatedValue
                    }));
                    
                    // Translate field name for alert
                    const fieldName = t[editingField] || editingField.charAt(0).toUpperCase() + editingField.slice(1);
                    alert(t.fieldUpdateSuccess.replace('{field}', fieldName));
                } else {
                    alert(t.fieldUpdateFailed.replace('{field}', editingField));
                }
            } catch (error) {
                console.error('Error updating profile field:', error);
                let errorMessage = t.errorUpdating.replace('{field}', editingField);
                if (error.response && error.response.data && error.response.data.message) {
                    errorMessage = error.response.data.message;
                }
                alert(errorMessage);
            }
        }
        // Close the overlay after saving
        setEditingField(null);
    };     

    const handleCancelEdit = () => {
        setEditingField(null);
    };

    const handleOverlayClick = (event) => {
        if (event.target.className === 'overlay') {
            setEditingField(null);
        }
    };

    const handleUpgradeSubscription = () => {
        alert('Upgrade to a paid subscription clicked!');
    };

    const handleEditBilling = () => {
        alert('Edit billing information clicked!');
    };

    const handleFavoriteOutfitUpload = (event) => {
        const file = event.target.files[0];
        if (file) {
            new Compressor(file, {
                quality: 0.6,
                success: (compressedFile) => {
                    const reader = new FileReader();
                    reader.onloadend = () => {
                        setProfile({ ...profile, favoriteOutfitImage: reader.result });
                    };
                    reader.readAsDataURL(compressedFile);
                },
                error: (err) => {
                    console.error('Error compressing image:', err);
                },
            });
        }
    };    

    if (loading) {
        return <div>{t.loading}</div>;
    }

    if (error) {
        return <div>{t.error}: {error}</div>;
    }

    if (profile === null || subscription === null) {
        return <div>{t.noProfileData}</div>;
    }

    // Find the current language object
    const currentLanguage = languages.find(lang => lang.code === language) || languages[0];

    return (
        <div className="profile-container">
            <h2>{t.profile}</h2>
            <div className="profile-content">
                <div className="profile-avatar">
                    <img
                        id="profile-image"
                        src={newAvatar ? URL.createObjectURL(newAvatar) : (profile.profile_image_url || defaultAvatar)}
                        alt="User Avatar"
                        className="avatar"
                    />
                    <div className="avatar-buttons">
                        <input type="file" id="profile-image-input" style={{ display: 'none' }} onChange={handleAvatarChange} />
                        <button className="avatar-button" onClick={() => document.getElementById('profile-image-input').click()}>
                            {t.addChange}
                        </button>
                    </div>
                </div>
                <div className="profile-info">
                    <p>
                        {t.gender}: <span>{profile.gender}</span> <i className="fas fa-user"></i>
                    </p>
                    <p>
                        {t.style}: <span>{profile.style}</span> <i className="fas fa-edit" onClick={() => handleEditClick('style')}></i>
                    </p>
                    <p>
                        {t.colors}: <span>{profile.colors}</span> <i className="fas fa-edit" onClick={() => handleEditClick('colors')}></i>
                    </p>
                    <p>
                        {t.city}: <span>{profile.city}</span> <i className="fas fa-edit" onClick={() => handleEditClick('city')}></i>
                    </p>
                    <p>
                        {t.email}: <span>{profile.email}</span> <i className="fas fa-edit" onClick={() => handleEditClick('email')}></i>
                    </p>
                    <p>
                        {t.language}: <span>{currentLanguage.name} {currentLanguage.flag}</span> 
                    </p>
                </div>
                {isChanged && (
                    <button className="save-button" onClick={handleSaveProfile}>{t.saveProfile}</button>
                )}
            </div>

            <div className="subscription-container settings-block">
                <h2><b>*</b>{t.settings}</h2>
                <p id='w'><b>{t.devBlockMessage}</b></p>

                {/* Language selection */}
                <div className="settings-item">
                    <label>{t.preferredLanguage}</label>
                    <p className="description-text">
                        {t.chooseLanguage}
                    </p>
                    <div className="language-selector">
                        <select 
                            value={language}
                            onChange={handleLanguageChange}
                            className="language-dropdown"
                        >
                            {languages.map(lang => (
                                <option key={lang.code} value={lang.code}>
                                    {lang.flag} {lang.name}
                                </option>
                            ))}
                        </select>
                        <div className="language-flag">
                            <span>{currentLanguage.flag}</span>
                        </div>
                    </div>
                </div>
                
                {/* Daily outfit time */}
                <div className="settings-item">
                    <label>{t.preferredTime}</label>
                    <p className="description-text">
                        {t.chooseDailyTime}
                    </p>
                    <input 
                        type="time" 
                        value={profile.dailyOutfitTime || '08:00'} 
                        onChange={(e) => {
                            setProfile({ ...profile, dailyOutfitTime: e.target.value });
                            setIsChanged(true);
                        }}
                        onBlur={() => document.activeElement.blur()}
                    />
                </div>

                {/* Favorite outfit upload */}
                <div className="settings-item">
                    <label>{t.uploadFavorite}</label>
                    <div className="upload-card" onClick={() => document.getElementById('favorite-outfit-input').click()}>
                        {profile.favoriteOutfitImage ? (
                            <img src={profile.favoriteOutfitImage} alt="Favorite Outfit" className="outfit-preview" />
                        ) : (
                            <p>{t.clickToAdd}</p>
                        )}
                    </div>
                    <input 
                        type="file" 
                        id="favorite-outfit-input" 
                        style={{ display: 'none' }} 
                        onChange={handleFavoriteOutfitUpload} 
                        accept="image/*" 
                    />
                    <p className="description-text">
                        {t.uploadDescription}
                    </p>
                </div>
                <div className='settings-button'>
                    <button className="save-button" onClick={handleSaveProfile}>{t.saveSettings}</button>
                </div>
            </div>

            <div className="subscription-container">
                <h2><b>*</b> {t.subscriptionDetails}</h2>
                <p id='w'><b>{t.freeAppMessage}</b></p>
                <p id='w'><b>{t.enjoyIt}</b></p>
                <div className="subscription-info">
                    <p>{t.plan}: <span>{subscription.planName}</span></p>
                    <p>{t.price}: <span>${subscription.price}/month</span></p>
                    <p>{t.nextBillingDate}: <span>{new Date(subscription.nextBillingDate).toLocaleDateString()}</span></p>
                    <p>{t.billingMethod}: <span>Visa **** 1234</span></p>
                    <div className="subscription-buttons">
                        <button className="upgrade-button" onClick={handleUpgradeSubscription}>{t.upgradePlan}</button>
                        <button className="edit-billing-button" onClick={handleEditBilling}>{t.updateBilling}</button>
                    </div>
                </div>
            </div>

            {editingField && (
                <div className="overlay" onClick={handleOverlayClick}>
                    <div className="edit-container">
                        <h3>{t.edit} {t[editingField] || editingField.charAt(0).toUpperCase() + editingField.slice(1)}</h3>
                        {editingField === 'email' ? (
                            <input type="email" value={newValue} onChange={handleFieldChange} />
                        ) : (
                            <select value={newValue} onChange={handleFieldChange}>
                                {editingField === 'style' && (
                                    <>
                                        <option value="Casual">Casual</option>
                                        <option value="Retro">Retro</option>
                                        <option value="Exotic">Exotic</option>
                                        <option value="Sporty">Sporty</option>
                                    </>
                                )}
                                {editingField === 'colors' && (
                                    <>
                                        <option value="Black, White">Black, White</option>
                                        <option value="Orange, White">Orange, White</option>
                                        <option value="Olive, White">Olive, White</option>
                                        <option value="Blue, Black">Blue, Black</option>
                                    </>
                                )}
                                {editingField === 'city' && (
                                    <>
                                        <option value="New York">New York</option>
                                        <option value="Chicago">Chicago</option>
                                        <option value="Houston">Houston</option>
                                        <option value="Los Angeles">Los Angeles</option>
                                    </>
                                )}
                            </select>
                        )}
                        <div className="edit-buttons">
                            <button className="save-button" onClick={handleSaveField}>{t.saveChanges}</button>
                            <button className="cancel-button" onClick={handleCancelEdit}>{t.cancel}</button>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
}

export default Profile;